import { useParams } from 'react-router-dom';
import { useState, useCallback } from 'react';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { useQueryClient } from '@tanstack/react-query';
import styles from './ChallengeFindingsPage.module.scss';
import LoadingSpinner from '../../components/LoadingSpinner';
import Finding from '../../components/finding/Finding';
import { useChallengeFindings } from '../../api/hooks/useChallengeFindings';
import { useOrganization } from '../../contexts/OrganizationContext';
import { challengeFindingService } from '../../api/services/challenge-finding.service';
import { ChallengeFindingDto } from '../../api/types/dtos/challenge';
import { useChallengePrograms } from '../../api/hooks/useChallengePrograms';

const FINDINGS_KEY = 'challenge-findings';

export const ChallengeFindingsPage: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [undoStack, _setUndoStack] = useState<{ action: string; data: any }[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const { data: findings, isLoading: findingsLoading, error: findingsError } = useChallengeFindings(
    selectedOrganization?.id || '',
    challengeId || '',
    true
  );

  const { data: challengePrograms, isLoading: programsLoading, error: programsError } = useChallengePrograms(
    selectedOrganization?.id || '',
    challengeId || ''
  );

  // Get the program version info from the first challenge program
  const challengeProgram = challengePrograms?.[0];
  const versionId = challengeProgram?.programVersionId;
  const programId = challengeProgram?.programId;

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const showMessage = (message: string) => {
    setSnackbar({ open: true, message });
  };

  const invalidateQueries = useCallback(() => {
    if (!selectedOrganization?.id || !challengeId) return;
    queryClient.invalidateQueries({
      queryKey: [FINDINGS_KEY, selectedOrganization.id, challengeId],
    });
  }, [selectedOrganization?.id, challengeId, queryClient]);

  const handleUpdate = useCallback(async (finding: ChallengeFindingDto) => {
    try {
      if (!finding.id || !challengeId || !selectedOrganization?.id) return;
      await challengeFindingService.update(selectedOrganization.id, challengeId, finding.id, finding);
      invalidateQueries();
      showMessage('Hallazgo de reto actualizado correctamente');
    } catch (error) {
      showMessage('Error actualizando hallazgo de reto');
      console.error('Error updating challenge finding:', error);
    }
  }, [challengeId, selectedOrganization?.id, invalidateQueries]);

  const handleAddFindingItem = useCallback(async (_challengeFindingId: string, _item: any) => {
    // Implement if you have challenge finding items logic
    showMessage('Funcionalidad de agregar criterio de reto no implementada');
  }, []);

  const handleRemoveFindingItem = useCallback(async (_challengeFindingId: string, _itemId: string) => {
    // Implement if you have challenge finding items logic
    showMessage('Funcionalidad de eliminar criterio de reto no implementada');
  }, []);

  const handleUndo = useCallback(async () => {
    // Implement undo logic if needed
    showMessage('Funcionalidad de deshacer no implementada');
  }, []);

  const handleDownloadCSV = useCallback(() => {
    // Implement CSV download logic if needed
    showMessage('Funcionalidad de descarga CSV no implementada');
  }, []);

  // Error and loading states
  const isLoading = findingsLoading || programsLoading;
  const errorMsg = findingsError?.message || programsError?.message;
  const missingProgramVersion = !versionId || !programId;

  return (
    <Container maxWidth="xl">
      {isLoading ? (
        <LoadingSpinner />
      ) : errorMsg ? (
        <Alert severity="error" className={styles.error}>
          {errorMsg}
        </Alert>
      ) : missingProgramVersion ? (
        <Alert severity="error" className={styles.error}>
          No se encontró información de versión de programa para este reto
        </Alert>
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <h1 className={styles.title}>Administra tus Hallazgos de Reto</h1>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                className={styles.downloadButton}
              >
                Descargar CSV
              </Button>
            </div>
            {undoStack.length > 0 && (
              <button onClick={handleUndo} className={styles.undoButton}>
                Deshacer última acción
              </button>
            )}
          </div>
          <div className={styles.content}>
            {findings?.map((finding: ChallengeFindingDto) => (
              <Finding
                key={finding.id}
                finding={finding}
                onUpdate={handleUpdate}
                onAddFindingItem={handleAddFindingItem}
                onRemoveFindingItem={handleRemoveFindingItem}
                organizationId={selectedOrganization?.id || ''}
                programId={programId}
                versionId={versionId}
              />
            ))}
          </div>
        </div>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Container>
  );
};

export default ChallengeFindingsPage; 